import {apiRetrieveImproved} from "@/api";
import {UpgradePlans} from "@/components/billing/UpgradePlans";
import PlusTrialCard from "@/components/billing/PlusTrialCard";
import {PASS_OFFER, passOfferOn} from "@/pricing/paymentsV3";
import {allInLine, PAYMENTS_V3, paymentsV3On} from "@/pricing/paymentsV3";
import Content from "@/components/Content";
import PButton from "@/components/PButton";
import {ClubSubscriptionTier, UserRole} from "@/constants";
import {GO_LIVE_GATE, PRICING_COPY, rateKeyForTier, tierLabel} from "@/pricing/plans";
import {Icon} from "@iconify/react";
import Header from "@/Header";
import {useClubSubscription} from "@/hooks/useClubSubscription";
import {useJwtAuth, useJwtUser} from "@/JWTAuthProvider";
import services, {PricingFeesResponse} from "@/services";
import {PublicClubProfile} from "@/types";
import queryKeys from "@/utilities/queryKeys";
import {Card, CardBody, CardHeader, Chip, Spinner} from "@heroui/react";
import {addToast} from "@heroui/toast";
import {useQuery} from "@tanstack/react-query";
import {createFileRoute, useNavigate, useParams} from "@tanstack/react-router";
import {useEffect, useState} from "react";
import {Crown, Receipt, Settings, Ticket} from "lucide-react";

export const Route = createFileRoute('/_app/clubs/$club_uid/billing')({
    component: RouteComponent,
});

/**
 * One line explaining what an unsubscribed club currently holds and why.
 *
 * The headline is their ACTUAL plan (Free). This says what is unlocked on top of it and until when,
 * so "Free" is never shown bare to somebody who can currently use Plus — which would read as the
 * features having been taken away.
 */
function entitlementNote(sub: ReturnType<typeof useClubSubscription>): string {
    const trial = (sub as any).trial;
    const days = trial?.days_left ?? 0;
    const dayWord = days === 1 ? 'day' : 'days';

    if (sub.entitlementSource === 'trial') {
        return `${trial?.tier_label ?? 'Plus'} features are unlocked — your free trial ends in ${days} ${dayWord}.`
            + ' Subscribe below to keep them.';
    }

    if (sub.entitlementSource === 'pre_trial') {
        return `${trial?.tier_label ?? 'Plus'} features are unlocked while you set up. Your `
            + `${trial?.trial_days ?? 14}-day free trial starts when you score your first game.`;
    }

    return "You're on the Free plan. Subscribe to unlock the match-day desk, leagues and your club tools.";
}

function RouteComponent() {
    const params = useParams({strict: false});
    const navigate = useNavigate();
    const jwt = useJwtAuth();
    const user = useJwtUser();
    const clubUid = params.club_uid!;

    useEffect(() => {
        jwt.reloadUser?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /*
     * Returning from a successful subscription checkout (?success=1).
     *
     * Nothing handled this before, and the single reloadUser() above is not enough on its own: it
     * fires the instant this page mounts, which is a race against Stripe's webhook. Lose the race —
     * which is the normal case, the redirect beats the webhook — and the client caches the OLD
     * entitlements and never asks again. The subscription is live server-side while the organizer
     * sits looking at a locked Match Centre, which is exactly what was reported.
     *
     * So: poll the subscription until the server agrees a subscription exists, THEN refresh the
     * user (allowed_to is an appended attribute on that payload, not a JWT claim, so a refetch is
     * genuinely enough) and drop the cached queries the gates read.
     */
    const [confirmingPurchase, setConfirmingPurchase] = useState(false);
    // Shown inline as well as in a toast: a toast is easy to miss, and "the button did nothing" is
    // the exact complaint this is fixing.
    const [portalError, setPortalError] = useState<string | null>(null);
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('success') !== '1') {
            return;
        }

        let cancelled = false;
        setConfirmingPurchase(true);

        // Bounded: ~20s covers a normal webhook comfortably. If it has not landed by then the
        // purchase is not lost — the webhook still applies it — so stop polling and let the page
        // render whatever the server currently says rather than spinning forever.
        const ATTEMPTS = 10;
        const GAP_MS = 2000;

        (async () => {
            for (let i = 0; i < ATTEMPTS && !cancelled; i++) {
                try {
                    const info: any = await services.clubSubscription.info(clubUid);
                    if (info?.plan?.is_subscribed) {
                        break;
                    }
                } catch {
                    // Keep polling: a transient failure here should not end the wait.
                }
                await new Promise((r) => setTimeout(r, GAP_MS));
            }

            if (cancelled) return;

            await jwt.reloadUser?.();
            await subscription.refetch();
            setConfirmingPurchase(false);

            // Clean the URL so a refresh does not re-run this.
            params.delete('success');
            const q = params.toString();
            window.history.replaceState({}, '', window.location.pathname + (q ? `?${q}` : ''));
            addToast({color: 'success', title: 'Subscription active — everything is unlocked.'});
        })();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // NOT queryKeys.profile(clubUid): that key is shared with the community and player profile
    // pages, whose queries return different payload shapes ({community: …}, a player) under the
    // same uid. Arriving here from the community profile served THAT page's cache — isFetched
    // true, data.club undefined — so the error screen showed on every first visit and "Try again"
    // (which refetches with THIS queryFn) always "fixed" it.
    const profileQuery = useQuery<PublicClubProfile>({
        queryKey: queryKeys.club_billing_profile(clubUid),
        queryFn:  async () => apiRetrieveImproved('public-club-profile' as any, {club_uid: clubUid}),
        enabled:  !!clubUid,
    });

    const pricingPlansQuery = useQuery({
        queryKey: queryKeys.pricing_plans(user?.uid ?? ''),
        queryFn:  () => services.pricing.getPlans(),
    });

    const feesQuery = useQuery({
        queryKey: ['pricing-fees'],
        queryFn:  () => services.pricing.getFees(),
    });

    const subscription = useClubSubscription(clubUid);

    const club = profileQuery.data?.club;

    // Watchdog for a request that never settles — see the loading guard below. 15s is well past a
    // slow-but-working load and well short of the user deciding the page is dead.
    const SLOW_MS = 15000;
    const [tookTooLong, setTookTooLong] = useState(false);
    const waiting = !club || subscription.isLoading;
    useEffect(() => {
        if (!waiting) {
            setTookTooLong(false);
            return;
        }
        const t = setTimeout(() => setTookTooLong(true), SLOW_MS);
        return () => clearTimeout(t);
    }, [waiting]);

    const clubOwnerUid = (club as any)?.owner_uid as string | undefined;
    const isOwner =
        (user?.club?.uid || user?.community?.uid) === clubUid
        || (!!clubOwnerUid && clubOwnerUid === user?.uid)
        || user?.role === UserRole.Master;

    /*
     * Loading, failed and slow are three different states and this used to render all of them as an
     * endless spinner.
     *
     * `!club` is true both while the profile is loading AND after it has FAILED — a failed query
     * leaves isLoading false and data undefined — so any error on either request pinned the page to
     * a spinner that could never resolve. That is the intermittent hang: nothing was still running,
     * there was simply nothing left to wait for. A reload "fixed" it because the retry succeeded.
     *
     * The watchdog covers the other half: a request that never settles at all keeps isLoading true
     * forever, and TanStack Query has no timeout of its own. After SLOW_MS we stop pretending and
     * offer the same retry.
     */
    // `isFetched`, NOT `!isFetching` — before the first fetch begins, isFetching is false and club is
    // undefined, so the latter would flash this error on every single load.
    const failed = profileQuery.isError || !!subscription.error || (profileQuery.isFetched && !club);
    const stillWaiting = !club || subscription.isLoading;

    if (failed || (stillWaiting && tookTooLong)) {
        return (
            <>
                <Header />
                <Content>
                    <div className="mx-auto max-w-md py-16 text-center">
                        <Icon icon="material-symbols-light:cloud-off-outline" className="mx-auto mb-3 text-5xl text-default-400" />
                        <p className="text-lg font-semibold">We couldn&rsquo;t load your billing details</p>
                        <p className="mt-1 text-sm text-default-500">
                            Your plan and any passes you own are unaffected — this screen just failed to load.
                        </p>
                        <PButton
                            className="mt-4"
                            color="primary"
                            onPress={() => {
                                setTookTooLong(false);
                                profileQuery.refetch();
                                subscription.refetch();
                            }}
                        >
                            Try again
                        </PButton>
                    </div>
                </Content>
            </>
        );
    }

    if (stillWaiting || confirmingPurchase) {
        return (
            <>
                <Header />
                <Content>
                    <div className="flex flex-col items-center gap-3 pt-12">
                        <Spinner size="lg" />
                        {confirmingPurchase && (
                            <p className="text-sm text-default-500">Confirming your subscription…</p>
                        )}
                    </div>
                </Content>
            </>
        );
    }

    if (!isOwner) {
        return (
            <>
                <Header />
                <Content>
                    <div className="text-center py-12">
                        <p className="text-lg text-default-500">
                            You don't have permission to view this club's billing settings.
                        </p>
                        <PButton className="mt-4" onPress={() => navigate({to: `/clubs/${clubUid}`})}>
                            Back to Club Profile
                        </PButton>
                    </div>
                </Content>
            </>
        );
    }

    return (
        <>
            <Header />
            <Content>
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold">Club Subscription</h2>
                    {subscription.isLegacyPricing && (
                        /* This club is on the ORIGINAL plan terms. The plan cards below are the
                           current line-up, so say plainly that theirs differ rather than letting
                           them read as what this club is on today. Subscribing moves them across. */
                        <div className="mt-2 rounded-lg border border-default-200 bg-default-50 px-3 py-2 text-sm text-default-600">
                            Your club is on our original plan terms. The plans shown below are our current
                            line-up — subscribing moves you onto them, which includes the free trial and
                            removes the old per-event entry limit.
                        </div>
                    )}
                    <PButton
                        variant="flat"
                        startContent={<Settings className="w-4 h-4" />}
                        onPress={() => navigate({to: `/clubs/${clubUid}`})}
                    >
                        Back to Club
                    </PButton>
                </div>

                {/* The $2.99 Plus trial: start, running (cancel in one click), or used. */}
                <PlusTrialCard clubUid={clubUid} trial={subscription.plusTrial} queryKey={queryKeys.club_subscription(clubUid) as unknown[]} />

                {/* Three across: Current Plan · Event Passes (coupon, centre) · Transaction fee. */}
                <div className="grid md:grid-cols-3 gap-6 mb-6 items-stretch">
                    {/* Current Plan */}
                    <Card className="h-full">
                        <CardHeader className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                                <Crown className="w-5 h-5 text-warning" />
                                <span className="font-semibold text-lg">Current Plan</span>
                            </div>
                            {/* The ACTUAL plan is the headline — what they pay for — not the
                                effective tier. Showing the effective tier here told a pre-trial club
                                its "Current Plan" was Plus, which is not a plan it holds and not a
                                thing it is being billed for. */}
                            <Chip
                                color={subscription.isSubscribed ? 'success' : 'default'}
                                variant="flat"
                            >
                                {tierLabel(subscription.actualTier, subscription.isLegacyPricing)}
                            </Chip>
                        </CardHeader>
                        <CardBody>
                            {subscription.isSubscribed ? (
                                <div className="space-y-2">
                                    <p className="text-success">Your subscription is active.</p>
                                    {subscription.expiresAt && (
                                        <p className="text-sm text-default-500">
                                            Next billing date: {new Date(subscription.expiresAt).toLocaleDateString()}
                                        </p>
                                    )}
                                    {/* Says what is actually behind it. "Manage Subscription" gave
                                        no clue that cancelling lived here, which is why it read as
                                        a dead button rather than a missing feature.

                                        NOT "change plan": the Stripe portal configuration has
                                        subscription_update DISABLED, so it offers cancellation,
                                        card updates and invoices but no plan switching. Promising
                                        a switch the portal will not show is the same mistake as
                                        advertising a price the checkout will not charge. */}
                                    <PButton
                                        size="sm"
                                        variant="flat"
                                        isLoading={subscription.isOpeningPortal}
                                        onPress={async () => {
                                            const error = await subscription.openBillingPortal();
                                            if (error) {
                                                setPortalError(error);
                                                addToast({color: 'danger', title: error});
                                            }
                                        }}
                                    >
                                        Manage or cancel subscription
                                    </PButton>
                                    {portalError && (
                                        <p className="text-xs text-danger-600">{portalError}</p>
                                    )}
                                    <p className="text-xs text-default-400">
                                        Opens Stripe, where you can update your card, download invoices or cancel.
                                        Cancelling keeps your plan until the end of the period you have paid for.
                                    </p>
                                </div>
                            ) : (
                                /* No subscription exists. Never say one is active here — that
                                   sentence is reserved for is_subscribed and nothing else. The trial
                                   or pre-trial window is stated as CONTEXT on the free plan, so the
                                   headline stays honest without hiding what they currently hold. */
                                <div className="space-y-2">
                                    <p className="text-default-500">
                                        {entitlementNote(subscription)}
                                    </p>
                                </div>
                            )}
                        </CardBody>
                    </Card>

                    {/* Event Passes — coupon in the centre */}
                    <EventPassCard />

                    {/* Transaction fee for this organizer's country + current plan */}
                    <TransactionFeeCard
                        fees={feesQuery.data}
                        countryCode={(club as any)?.country_code || user?.country_code}
                        tier={subscription.tier}
                        actualTier={subscription.actualTier}
                        trialing={subscription.entitlementSource === 'trial' || subscription.entitlementSource === 'pre_trial'}
                        override={(user as any)?.fee_override}
                    />
                </div>

                {/* Upgrade Options — anyone below the top plan we actually sell them can move up.
                    That ceiling is Pro for a legacy club and Plus on the current model, where Pro is
                    no longer sold. It has to be a ceiling rather than a flat `< Pro`: with Pro hidden,
                    a model-2 club already on Plus would otherwise render an "Upgrade Your Plan"
                    heading over an empty grid.

                    Keyed on the ACTUAL plan, not the effective tier. Off the effective tier a club
                    on a Plus trial looked like a Plus subscriber, so every plan was "already held"
                    and the whole section vanished — leaving the organizer no way to subscribe during
                    precisely the window in which they are most likely to. */}
                {subscription.actualTier < (subscription.isLegacyPricing ? ClubSubscriptionTier.Pro : ClubSubscriptionTier.Starter) && (
                    <UpgradePlans
                        currentTier={subscription.actualTier}
                        isLegacy={subscription.isLegacyPricing}
                        onUpgrade={handleUpgrade}
                        isLoading={subscription.isCheckingOut}
                        pricingData={pricingPlansQuery.data}
                        isPricingLoaded={pricingPlansQuery.isSuccess}
                        fees={feesQuery.data}
                        countryCode={(club as any)?.country_code || user?.country_code}
                    />
                )}
            </Content>
        </>
    );

    async function handleUpgrade(plan: number) {
        try {
            const currencyCode = pricingPlansQuery.data?.[plan]?.currency_code ?? 'USD';
            const result = await subscription.checkout({
                plan,
                currencyCode,
            });
            if (result.redirect_url) {
                window.location.href = result.redirect_url;
            } else {
                addToast({color: 'danger', title: 'Could not start checkout. Please try again.'});
            }
        } catch (error) {
            console.error('Checkout failed:', error);
            addToast({color: 'danger', title: (error as any)?.message || 'Could not start checkout. Please try again.'});
        }
    }
}

function EventPassCard() {
    // Under the go-live gate the pass is sized: the card says what the tiers are and that a pass
    // bought here is the first tier, held for the next event.
    const gateOn = !!(useJwtUser() as any)?.golive_gate;
    const statusQuery = useQuery({
        queryKey: ['event-pass-status'],
        queryFn:  () => services.eventPass.status(),
    });
    const [busy, setBusy] = useState(false);

    // On return from Stripe (?event_pass=success&session_id=…) confirm the purchase immediately so
    // the pass shows without waiting on the webhook, then clean the URL and refresh the count.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('event_pass') !== 'success') return;
        const sessionId = params.get('session_id');
        const finish = () => {
            params.delete('event_pass');
            params.delete('session_id');
            const q = params.toString();
            window.history.replaceState({}, '', window.location.pathname + (q ? `?${q}` : ''));
            statusQuery.refetch();
        };
        if (sessionId) {
            services.eventPass.confirm(sessionId)
                .then((r) => { if (r.created) addToast({color: 'success', title: 'Event Pass added.'}); })
                .catch(() => {})
                .finally(finish);
        } else {
            finish();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // "Have a code?" (pass_offer_enabled): the one-time offer's code, prefilled when the email's
    // link had to come through login (?pass_offer=CODE). The server says what it is worth, or why
    // it no longer works; a valid code rides with the checkout and Stripe applies the discount.
    const [code, setCode] = useState(() => {
        try {
            return (new URLSearchParams(window.location.search).get('pass_offer') || '').toUpperCase();
        } catch {
            return '';
        }
    });
    const [checkCode, setCheckCode] = useState(code);
    const offerQuery = useQuery<any>({
        queryKey: ['pass-offer-check', checkCode],
        queryFn:  () => services.eventPass.status({offer_code: checkCode} as any),
        enabled:  passOfferOn() && !!checkCode,
    });
    const offer: {code: string; valid: boolean; reason: string | null; message: string | null; percent: number; discounted_cents: number | null} | null = offerQuery.data?.offer ?? null;
    const offerCode = offer?.valid ? offer.code : undefined;
    const discounted = (cents: number) => offer?.valid ? Math.round(cents * (100 - offer.percent) / 100) : cents;

    const buy = async (tier?: number) => {
        setBusy(true);
        // The tab is opened SYNCHRONOUSLY, before the await. A window.open() after an await is a
        // popup the browser did not see the click for, and it gets blocked. If it is blocked anyway,
        // fall back to navigating this tab rather than silently doing nothing.
        const tab = window.open('', '_blank');
        try {
            const {redirect_url} = await services.eventPass.checkout(window.location.pathname + window.location.search, undefined, 'standard', undefined, undefined, tier, offerCode);
            if (tab && !tab.closed) {
                tab.location.href = redirect_url;
                setBusy(false);
            } else {
                window.location.href = redirect_url;
            }
        } catch (e: any) {
            tab?.close();
            addToast({color: 'danger', title: e?.message || 'Could not start checkout. Please try again.'});
            setBusy(false);
        }
    };

    const available = statusQuery.data?.available ?? 0;
    const used = statusQuery.data?.used ?? 0;
    // Under the gate: the tiers Billing sells, and the held passes by tier ("1 × up to 16, 1 × 17–32").
    const tiers: {tier: number; label: string; cents: number; currency: string}[] = (statusQuery.data as any)?.tiers ?? [];
    const heldByTier: Record<string, number> = (statusQuery.data as any)?.held_by_tier ?? {};
    const heldLine = tiers.filter((t) => (heldByTier[String(t.tier)] ?? 0) > 0)
        .map((t) => `${heldByTier[String(t.tier)]} × ${t.label.replace(' entries', '')}`).join(', ');
    const fmtCents = (cents: number, currency: string) => {
        try { return new Intl.NumberFormat(undefined, {style: 'currency', currency: currency.toUpperCase()}).format(cents / 100); }
        catch { return `${currency.toUpperCase()} ${(cents / 100).toFixed(2)}`; }
    };

    // `effective_price` — what THIS surface will be charged, priced by the same server method the
    // checkout uses.
    //
    // This previously read `promo`, which was wrong in the way that matters: the promo is opt-in
    // per request and only the quiz funnel passes the code, so this card advertised US$4.99 while
    // the checkout it starts charged US$9.99. Reading the effective price makes the two impossible
    // to separate — if this surface ever should offer the promo, it has to pass the code to BOTH
    // status() and checkout(), and the displayed price follows automatically.
    const price = (statusQuery.data as any)?.effective_price ?? (statusQuery.data as any)?.price;
    const priceLabel = price
        ? (() => {
            try {
                return new Intl.NumberFormat(undefined, {
                    style: 'currency', currency: (price.currency || 'usd').toUpperCase(),
                }).format(price.amount_cents / 100);
            } catch {
                return `${(price.currency || 'usd').toUpperCase()} ${(price.amount_cents / 100).toFixed(2)}`;
            }
        })()
        : null;
    // NOT `promo`: this surface does not send the code, so it is not getting that price. Saying
    // "half price" here while charging full price is the exact mismatch this whole block fixes.
    const hasPromo = false;

    // The Event Pass waived (event_pass_waived): nothing to sell, so no card at all.
    if ((statusQuery.data as any)?.waived) return null;

    return (
        // Coupon-styled: yellow border, ticket notches, perforated divider.
        <div className="relative h-full rounded-2xl border-2 border-dashed border-[#E6C200] bg-[#FFFCEC] p-5 flex flex-col overflow-visible">
            {/* ticket notches (coloured as the page background) */}
            <span className="pointer-events-none absolute top-1/2 -translate-y-1/2 -left-[13px] h-6 w-6 rounded-full bg-[#EDEEF4]" aria-hidden />
            <span className="pointer-events-none absolute top-1/2 -translate-y-1/2 -right-[13px] h-6 w-6 rounded-full bg-[#EDEEF4]" aria-hidden />

            <div className="flex items-center gap-2 mb-2">
                <Ticket className="w-5 h-5 text-[#9a7d00]" />
                <span className="font-semibold text-lg">Event Passes</span>
            </div>
            <div className="flex items-baseline gap-2">
                <span className="text-4xl font-extrabold text-[#1a1a2e]">{available}</span>
                <span className="text-default-500 text-sm">available{used ? ` · ${used} used` : ''}</span>
            </div>
            {gateOn && heldLine && <div className="text-xs text-default-500 mt-0.5" data-testid="held-by-tier">{heldLine}</div>}
            {/* Model-2 copy. This used to describe an 8-player cap on Pay-as-you-go, which is a
                legacy limit that does not exist under the current plans — so it invented a
                restriction the organizer does not have and misdescribed what the pass is for. */}
            {gateOn ? (
                <div className="flex-1">
                    <p className="text-default-500 text-sm mt-2">{GO_LIVE_GATE.billingPassesCopy}</p>
                    {/* Event-level, as the Plus card's bullets are club-level: the one sentence's two halves. */}
                    <ul className="mt-2 space-y-0.5 text-xs text-default-600" data-testid="event-pass-features">
                        {PRICING_COPY.eventPassFeatures.map((b) => <li key={b}>✓ {b}</li>)}
                    </ul>
                </div>
            ) : (
                <p className="text-default-500 text-sm mt-2 flex-1">
                    <strong>Unlimited access for one event.</strong> One pass covers one cash or free-entry event,
                    however many players enter. Events that take payment through ScorePal never need one.
                </p>
            )}
            {priceLabel && (
                <p className="mt-2 text-sm font-semibold text-[#1a1a2e]">
                    {priceLabel} each{' '}
                    {hasPromo && <span className="font-normal text-default-500">— your first pass, half price</span>}
                </p>
            )}
            {passOfferOn() && (
                <div className="mt-2 text-xs" data-pass-offer-box>
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold">{PASS_OFFER.haveCode}</span>
                        <input value={code} onChange={(e) => setCode(e.target.value)} placeholder={PASS_OFFER.placeholder} maxLength={12} aria-label={PASS_OFFER.haveCode}
                               className="w-32 rounded-md border border-default-200 bg-content1 px-2 py-1 font-mono text-xs uppercase outline-none focus:border-primary" />
                        <PButton size="sm" variant="flat" onPress={() => setCheckCode(code.trim().toUpperCase())} isLoading={offerQuery.isFetching} isDisabled={!code.trim() || busy}>{PASS_OFFER.apply}</PButton>
                    </div>
                    {offer && checkCode === code.trim().toUpperCase() && (
                        <div className={'mt-1 ' + (offer.valid ? 'font-semibold text-success' : 'text-danger')} data-pass-offer-result>
                            {offer.valid ? `${offer.percent}% off applied to one Event Pass of any size.` : offer.message}
                        </div>
                    )}
                </div>
            )}
            <div className="border-t-2 border-dashed border-[#E6C200]/60 my-3" />
            {gateOn && tiers.length > 0 ? (
                <div className="grid gap-2" data-testid="pass-tiers">
                    {tiers.map((t) => (
                        <PButton key={t.tier} className="w-full justify-between bg-[#082F8A] text-white font-semibold hover:bg-[#06246b]" onPress={() => buy(t.tier)} isLoading={busy}>
                            <span>{t.label.charAt(0).toUpperCase() + t.label.slice(1)}</span>
                            <span>{offerCode ? <><s className="mr-1 opacity-70">{fmtCents(t.cents, t.currency)}</s>{fmtCents(discounted(t.cents), t.currency)}</> : fmtCents(t.cents, t.currency)}</span>
                        </PButton>
                    ))}
                </div>
            ) : (
                <PButton className="w-full bg-[#082F8A] text-white font-semibold hover:bg-[#06246b]" onPress={() => buy()} isLoading={busy}>
                    Buy an Event Pass{priceLabel ? ` — ${priceLabel}` : ''}
                </PButton>
            )}
        </div>
    );
}

function TransactionFeeCard({fees, countryCode, tier, actualTier, trialing, override}: {
    fees?: PricingFeesResponse;
    countryCode?: string | null;
    /** EFFECTIVE tier — the rate they are charged today. */
    tier: number;
    /** ACTUAL plan — the rate they fall back to when the trial ends. */
    actualTier: number;
    trialing: boolean;
    /** Master-set special rate (auth/me fee_override) — replaces either schedule part. */
    override?: {rate?: number | null; fixed_cents?: number | null} | null;
}) {
    // Payments v3, an Express account (or none yet): the all-in fee is the whole story.
    const payout = useQuery({queryKey: ['payout-account-status'], queryFn: () => services.wallet.payoutAccountStatus(), staleTime: 60_000, enabled: paymentsV3On()});
    if (paymentsV3On() && payout.data && payout.data.account_type !== 'standard') {
        const plus = tier >= ClubSubscriptionTier.Starter;
        const currency = fees?.schedule[(countryCode || fees.default_country || 'US').toUpperCase()]?.currency || 'USD';
        return (
            <Card className="h-full">
                <CardHeader className="flex items-center gap-2">
                    <Receipt className="w-5 h-5 text-primary" />
                    <span className="font-semibold text-lg">Online entry fees</span>
                </CardHeader>
                <CardBody className="space-y-2">
                    <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-bold">{allInLine(currency, plus)}</span>
                        <span className="text-default-500">all-in</span>
                    </div>
                    <p className="text-default-500 text-sm">{PAYMENTS_V3.feeSentence(currency)}</p>
                    {payout.data.collect_on_the_day && <p className="text-xs text-default-400">{PAYMENTS_V3.collectTitle}.</p>}
                </CardBody>
            </Card>
        );
    }
    if (!fees) return null;

    const cc = (countryCode || fees.default_country || 'US').toUpperCase();
    const row = fees.schedule[cc] ?? fees.schedule[fees.default_country] ?? fees.schedule['US'];
    if (!row) return null;

    // Via the shared helper rather than a hand-written ternary — the plan_rates keys are offset from
    // the enum names and mapping them by hand here is what produced two different wrong rates in the
    // upgrade modal.
    const rate = override?.rate ?? row.plan_rates[rateKeyForTier(tier)];
    const afterTrialRate = override?.rate ?? row.plan_rates[rateKeyForTier(actualTier)];
    // A trial club is charged the trial tier's rate, which silently reverts when the trial ends.
    // Presenting it as a settled rate is a promise we do not keep, so say both numbers. An
    // overridden rate never reverts — it is pinned regardless of plan.
    const rateReverts = trialing && afterTrialRate !== rate;
    const fixed = override?.fixed_cents != null ? override.fixed_cents / 100 : row.fixed_cents / 100;

    const fmt = (n: number) => {
        try {
            return new Intl.NumberFormat(undefined, {style: 'currency', currency: row.currency}).format(n);
        } catch {
            return `${row.currency} ${n.toFixed(2)}`;
        }
    };

    return (
        <Card className="h-full">
            <CardHeader className="flex items-center gap-2">
                <Receipt className="w-5 h-5 text-primary" />
                <span className="font-semibold text-lg">Your transaction fee</span>
            </CardHeader>
            <CardBody className="space-y-2">
                <p className="text-default-500 text-sm">
                    Charged per paid registration on your current plan ({cc}). Absorb it or pass it to players at checkout.
                </p>
                <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold">{rate}%</span>
                    <span className="text-default-500">+ {fmt(fixed)} per registration</span>
                </div>
                {rateReverts && (
                    <p className="rounded-lg bg-warning-50 px-2 py-1.5 text-xs font-medium text-warning-800 dark:bg-warning-950/30">
                        {rate}% while your trial is active — {afterTrialRate}% after it ends unless you subscribe.
                    </p>
                )}
                <p className="text-xs text-default-400">
                    e.g. on a {fmt(50)} entry that's {fmt(50 * rate / 100 + fixed)} per player. Higher plans pay a lower rate.
                </p>
            </CardBody>
        </Card>
    );
}
